import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { sitesApi, type CreateWellRequest } from '@/api/sites';
import { flagCodesApi } from '@/api/flagCodes';
import type { AxiosError } from 'axios';

const WELL_TYPES = [
  { value: 'monitoring', label: 'Monitoring (MW)' },
  { value: 'recovery', label: 'Recovery (RW)' },
  { value: 'mdpe', label: 'MDPE' },
  { value: 'soil_boring', label: 'Soil Boring (SB)' },
];

const CASING_MATERIALS = ['', 'PVC', 'Steel', 'Stainless Steel', 'HDPE', 'Other'];

const STATUS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'abandoned', label: 'Abandoned' },
];

interface FormState {
  well_name: string;
  well_type: 'monitoring' | 'recovery' | 'mdpe' | 'soil_boring';
  latitude: string;
  longitude: string;
  total_depth_ft: string;
  screen_top_ft: string;
  screen_bottom_ft: string;
  casing_diameter_in: string;
  casing_elevation_ft: string;
  ground_elevation_ft: string;
  casing_material: string;
  install_date: string;
  status: 'active' | 'inactive' | 'abandoned';
  // Optional initial reading
  add_reading: boolean;
  measurement_date: string;
  depth_to_water_ft: string;
  depth_to_product_ft: string;
  flag_code: string;
  reading_notes: string;
}

const EMPTY: FormState = {
  well_name: '',
  well_type: 'monitoring',
  latitude: '',
  longitude: '',
  total_depth_ft: '',
  screen_top_ft: '',
  screen_bottom_ft: '',
  casing_diameter_in: '',
  casing_elevation_ft: '',
  ground_elevation_ft: '',
  casing_material: '',
  install_date: '',
  status: 'active',
  add_reading: false,
  measurement_date: '',
  depth_to_water_ft: '',
  depth_to_product_ft: '',
  flag_code: '',
  reading_notes: '',
};

interface Props {
  siteId: string;
  open: boolean;
  onClose: () => void;
  onCreated?: (wellId: string) => void;
}

export function AddWellModal({ siteId, open, onClose, onCreated }: Props) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  // Tenant-scoped gauging flag codes for the initial-reading dropdown.
  // Server filters by ctx.client_id, so a user only ever sees their own
  // tenant's codes. ``enabled: open`` defers the fetch until the modal
  // is actually visible.
  const { data: gaugingCodes = [] } = useQuery({
    queryKey: ['flag-codes', 'gauging'],
    queryFn: () => flagCodesApi.list('gauging'),
    enabled: open,
    staleTime: 60_000,
  });

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (fieldErrors[key as string]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setFieldErrors({});
    setTopError(null);
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateWellRequest) => sitesApi.createWell(siteId, payload),
    onSuccess: (well) => {
      queryClient.invalidateQueries({ queryKey: ['site-wells', siteId] });
      onCreated?.(well.id);
      reset();
      onClose();
    },
    onError: (err: AxiosError<{ detail?: string }>) => {
      const detail = err.response?.data?.detail || err.message || 'Failed to create well';
      const status = err.response?.status;
      if (status === 409) {
        setFieldErrors({ well_name: detail });
      } else if (status === 400 && /flag_code/i.test(detail)) {
        setFieldErrors({ flag_code: detail });
      } else {
        setTopError(detail);
      }
    },
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.well_name.trim()) errs.well_name = 'Required';
    if (!form.latitude.trim()) errs.latitude = 'Required';
    else {
      const n = parseFloat(form.latitude);
      if (isNaN(n) || n < -90 || n > 90) errs.latitude = 'Must be between -90 and 90';
    }
    if (!form.longitude.trim()) errs.longitude = 'Required';
    else {
      const n = parseFloat(form.longitude);
      if (isNaN(n) || n < -180 || n > 180) errs.longitude = 'Must be between -180 and 180';
    }
    if (form.add_reading) {
      if (!form.measurement_date) errs.measurement_date = 'Required when adding a reading';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTopError(null);
    if (!validate()) return;

    const payload: CreateWellRequest = {
      well_name: form.well_name.trim(),
      well_type: form.well_type,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      status: form.status,
    };
    const num = (s: string) => (s.trim() ? parseFloat(s) : undefined);
    if (num(form.total_depth_ft) !== undefined) payload.total_depth_ft = num(form.total_depth_ft);
    if (num(form.screen_top_ft) !== undefined) payload.screen_top_ft = num(form.screen_top_ft);
    if (num(form.screen_bottom_ft) !== undefined) payload.screen_bottom_ft = num(form.screen_bottom_ft);
    if (num(form.casing_diameter_in) !== undefined) payload.casing_diameter_in = num(form.casing_diameter_in);
    if (num(form.casing_elevation_ft) !== undefined) payload.casing_elevation_ft = num(form.casing_elevation_ft);
    if (num(form.ground_elevation_ft) !== undefined) payload.ground_elevation_ft = num(form.ground_elevation_ft);
    if (form.casing_material) payload.casing_material = form.casing_material;
    if (form.install_date) payload.install_date = form.install_date;

    if (form.add_reading) {
      payload.initial_reading = {
        measurement_date: form.measurement_date,
      };
      if (num(form.depth_to_water_ft) !== undefined) {
        payload.initial_reading.depth_to_water_ft = num(form.depth_to_water_ft);
      }
      if (num(form.depth_to_product_ft) !== undefined) {
        payload.initial_reading.depth_to_product_ft = num(form.depth_to_product_ft);
      }
      if (form.flag_code) payload.initial_reading.flag_code = form.flag_code;
      if (form.reading_notes.trim()) payload.initial_reading.notes = form.reading_notes.trim();
    }

    createMutation.mutate(payload);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-mono-950 border border-mono-700 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-mono-800">
          <h2 className="text-sm font-medium tracking-widest text-mono-100">ADD WELL</h2>
          <button
            type="button"
            onClick={() => { reset(); onClose(); }}
            className="text-mono-500 hover:text-mono-300"
            aria-label="Close"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-5">
          {topError && (
            <div className="border border-red-900/50 bg-red-950/30 rounded px-3 py-2 text-sm text-red-300">
              {topError}
            </div>
          )}

          {/* Identity */}
          <Section title="IDENTITY">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Well Name" required
                value={form.well_name}
                onChange={(v) => update('well_name', v)}
                error={fieldErrors.well_name}
                hint="e.g. MW-01"
              />
              <Select
                label="Well Type"
                value={form.well_type}
                onChange={(v) => update('well_type', v as FormState['well_type'])}
                options={WELL_TYPES}
              />
            </div>
            <Select
              label="Status"
              value={form.status}
              onChange={(v) => update('status', v as FormState['status'])}
              options={STATUS}
            />
          </Section>

          {/* Location */}
          <Section title="LOCATION">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Latitude" required type="number" step="0.000001"
                value={form.latitude}
                onChange={(v) => update('latitude', v)}
                error={fieldErrors.latitude}
                hint="Decimal degrees"
              />
              <Input
                label="Longitude" required type="number" step="0.000001"
                value={form.longitude}
                onChange={(v) => update('longitude', v)}
                error={fieldErrors.longitude}
                hint="Decimal degrees"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Ground Elevation" type="number" step="0.01"
                value={form.ground_elevation_ft}
                onChange={(v) => update('ground_elevation_ft', v)}
                hint="ft MSL"
              />
              <Input
                label="Casing Elevation" type="number" step="0.001"
                value={form.casing_elevation_ft}
                onChange={(v) => update('casing_elevation_ft', v)}
                hint="ft MSL (TOC)"
              />
            </div>
          </Section>

          {/* Construction */}
          <Section title="CONSTRUCTION">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Total Depth" type="number" step="0.01"
                value={form.total_depth_ft}
                onChange={(v) => update('total_depth_ft', v)}
                hint="ft"
              />
              <Input
                label="Casing Diameter" type="number" step="0.01"
                value={form.casing_diameter_in}
                onChange={(v) => update('casing_diameter_in', v)}
                hint="inches"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Screen Top" type="number" step="0.01"
                value={form.screen_top_ft}
                onChange={(v) => update('screen_top_ft', v)}
                hint="ft below ground"
              />
              <Input
                label="Screen Bottom" type="number" step="0.01"
                value={form.screen_bottom_ft}
                onChange={(v) => update('screen_bottom_ft', v)}
                hint="ft below ground"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Casing Material"
                value={form.casing_material}
                onChange={(v) => update('casing_material', v)}
                options={CASING_MATERIALS.map(m => ({ value: m, label: m || '--' }))}
              />
              <Input
                label="Install Date" type="date"
                value={form.install_date}
                onChange={(v) => update('install_date', v)}
              />
            </div>
          </Section>

          {/* Optional initial reading */}
          <Section title="INITIAL READING (OPTIONAL)">
            <label className="flex items-center gap-2 text-xs tracking-wider text-mono-400 cursor-pointer">
              <input
                type="checkbox"
                checked={form.add_reading}
                onChange={(e) => update('add_reading', e.target.checked)}
                className="h-3.5 w-3.5 accent-cyan-500"
              />
              Record an initial water-level reading
            </label>
            {form.add_reading && (
              <div className="space-y-3 pl-5 border-l border-mono-800">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Measurement Date" required type="date"
                    value={form.measurement_date}
                    onChange={(v) => update('measurement_date', v)}
                    error={fieldErrors.measurement_date}
                  />
                  <Select
                    label="Flag Code"
                    value={form.flag_code}
                    onChange={(v) => update('flag_code', v)}
                    options={[
                      { value: '', label: '--' },
                      ...gaugingCodes
                        .filter((c) => c.is_active)
                        .map((c) => ({ value: c.code, label: c.code })),
                    ]}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Depth to Water" type="number" step="0.001"
                    value={form.depth_to_water_ft}
                    onChange={(v) => update('depth_to_water_ft', v)}
                    hint="ft below TOC"
                  />
                  <Input
                    label="Depth to Product" type="number" step="0.001"
                    value={form.depth_to_product_ft}
                    onChange={(v) => update('depth_to_product_ft', v)}
                    hint="ft (if LNAPL present)"
                  />
                </div>
                <Input
                  label="Notes"
                  value={form.reading_notes}
                  onChange={(v) => update('reading_notes', v)}
                />
                {fieldErrors.flag_code && (
                  <p className="text-xs text-red-400">{fieldErrors.flag_code}</p>
                )}
              </div>
            )}
          </Section>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-mono-800">
          <button
            type="button"
            onClick={() => { reset(); onClose(); }}
            className="px-4 py-2 text-xs tracking-wider text-mono-400 border border-mono-700 rounded hover:bg-mono-800 transition-colors"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="px-4 py-2 text-xs tracking-wider font-medium text-cyan-400 border border-cyan-500/40 rounded hover:bg-cyan-500/10 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? 'SAVING...' : 'SAVE WELL'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Helpers ---------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-[10px] tracking-widest text-mono-500 border-b border-mono-800 pb-1.5">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Input({
  label, required, value, onChange, error, hint, type = 'text', step,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  type?: string;
  step?: string;
}) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider text-mono-400">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full bg-mono-950 border ${
          error ? 'border-red-500/60' : 'border-mono-700'
        } rounded px-2.5 py-1.5 text-sm text-mono-200 focus:border-cyan-500 focus:outline-none`}
      />
      {error && <span className="block mt-1 text-xs text-red-400">{error}</span>}
      {!error && hint && <span className="block mt-1 text-[10px] text-mono-600">{hint}</span>}
    </label>
  );
}

function Select({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider text-mono-400">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-mono-950 border border-mono-700 rounded px-2.5 py-1.5 text-sm text-mono-200 focus:border-cyan-500 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
